import { Formik } from 'formik';
import { useCallback, useMemo, useState } from 'react';

import { useGetContentManagementAppsQuery } from '@app/api/apps';
import { useGetLanguagesQuery } from '@app/api/languages';
import { useCreateProjectMutation } from '@app/api/newProject';
import { showUnexpectedErrorToast } from '@app/components/Toast';
import track, { AnalyticsEventName } from '@app/utils/analytics';

import { ProjectType } from '../../../../types/project';
import type { CreateFormProps } from './CreateForm';
import CreateForm from './CreateForm';
import { CreateProjectSchema } from './schemas';

type ErrorResponseViolation = {
    [key: string]: string[];
};

type ErrorResponse = {
    error: {
        data: {
            error: {
                message: string;
                code: number;
                violations: ErrorResponseViolation;
            };
        };
    };
    status: number;
};

type LanguageOption = {
    value: string;
    label: string;
};

type ContentIntegrationAppOption = {
    value: string;
    label: string;
    disabled: boolean;
};

const projectTypeView = {
    [ProjectType.localizationFiles]: 'localization',
    [ProjectType.pagedDocuments]: 'documents',
    [ProjectType.contentIntegration]: 'content-integration',
} as Record<string, string>;

const initialSourceLanguage: LanguageOption = { value: '640', label: 'English (en)' };

export type SubmitValues = {
    name: string;
    sourceLanguage: LanguageOption;
    targetLanguages: LanguageOption[];
    projectType: string;
    isSegmentationEnabled: boolean;
    onboardingFlow: boolean;
    segmentationRulesType: string;
    segmentationRulesFile: File | undefined;
    contentIntegration: ContentIntegrationAppOption;
};

export const initialFormValues: SubmitValues = {
    name: '',
    sourceLanguage: initialSourceLanguage,
    targetLanguages: [],
    projectType: '0',
    isSegmentationEnabled: false,
    onboardingFlow: false,
    segmentationRulesType: 'default',
    segmentationRulesFile: undefined,
    contentIntegration: { value: '', label: '', disabled: false },
};

type CreateFormContainerProps = Omit<CreateFormProps, 'onSubmit' | 'loading' | 'languagesLoading' | 'languages'> & {
    initialValues?: typeof initialFormValues;
    onSubmit?: (projectId: string) => void;
    loading?: boolean;
    selectedProjectType?: string;
    goBackToSelectionScreen?: () => void;
};

const CreateFormContainer = ({
    initialValues = initialFormValues,
    minified = false,
    onSubmit,
    loading = false,
    selectedProjectType = '',
    ...rest
}: CreateFormContainerProps) => {
    const [createProjectLoading, setCreateProjectLoading] = useState(false);
    const { isLoading: languagesLoading, data: languages } = useGetLanguagesQuery(undefined);
    const { isLoading: contentIntegrationAppsLoading, data: integrationApps } = useGetContentManagementAppsQuery(
        { projectType: selectedProjectType },
        {
            skip: selectedProjectType !== ProjectType.contentIntegration,
        },
    );

    const contentIntegrationApps = useMemo(
        () => integrationApps?.filter((app) => app.isSingleInstallApp),
        [integrationApps],
    );

    const [createProject] = useCreateProjectMutation();

    const submitHandler = useCallback(
        async (
            {
                name,
                sourceLanguage,
                targetLanguages,
                projectType,
                isSegmentationEnabled,
                onboardingFlow,
                segmentationRulesType,
                segmentationRulesFile,
                contentIntegration,
            }: SubmitValues,
            setFieldError: (field: string, message: string) => void,
        ) => {
            setCreateProjectLoading(true);
            const response = await createProject({
                name,
                sourceLanguage: parseInt(sourceLanguage.value, 10),
                targetLanguages: targetLanguages.map(({ value }) => parseInt(value, 10)),
                projectType: parseInt(projectType, 10),
                reviewing: true,
                isSegmentationEnabled,
                onboardingFlow,
                segmentationRulesType,
                segmentationRulesFile,
                contentIntegration: contentIntegration.value,
            });

            if ('error' in response) {
                const violations = (response as ErrorResponse)?.error.data.error?.violations as ErrorResponseViolation;

                if (violations && 'segmentationRulesFile' in violations) {
                    setFieldError('segmentationRulesFile', violations.segmentationRulesFile.toString());
                } else {
                    showUnexpectedErrorToast();
                }

                setCreateProjectLoading(false);

                return;
            }

            const { baseLanguage, reviewing, projectName, projectId, projectRole } = response.data;

            if (process.env.NODE_ENV !== 'test') {
                track(AnalyticsEventName.PROJECT_CREATED, {
                    base_language: baseLanguage,
                    project_type: projectTypeView[projectType],
                    project_name: projectName,
                    project_id: projectId,
                    project_role: projectRole,
                    reviewing,
                    is_segmentation_enabled: isSegmentationEnabled,
                });
            }

            // default submit behavior
            if (!onSubmit) {
                if (projectType === ProjectType.pagedDocuments) {
                    window.location.assign(`/upload/${projectId}/`);
                } else if (projectType === ProjectType.contentIntegration) {
                    window.location.assign(`/apps/${projectId}/${contentIntegration.value}`);
                } else {
                    window.location.assign(`/project/${projectId}/?view=multi`);
                }
            }

            if (onSubmit) {
                onSubmit?.(projectId);
            }
        },
        [createProject, onSubmit],
    );

    return (
        <Formik<SubmitValues>
            initialValues={initialValues}
            validateOnChange={false}
            validateOnBlur={false}
            validationSchema={CreateProjectSchema}
            onSubmit={(values, { setFieldError }) => {
                submitHandler(values, setFieldError);
            }}
        >
            <CreateForm
                loading={createProjectLoading || loading}
                languages={languages || []}
                languagesLoading={languagesLoading}
                minified={minified}
                selectedProjectType={selectedProjectType}
                contentIntegrationAppsLoading={contentIntegrationAppsLoading}
                contentIntegrationApps={contentIntegrationApps}
                {...rest}
            />
        </Formik>
    );
};

export { ProjectNameSchema, CreateProjectSchema, LanguagesSchema } from './schemas';

export default CreateFormContainer;
